import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';

const createLoginSchema = (t: (key: string) => string) => z.object({
  email: z
    .string()
    .min(1, t('validation:required'))
    .email(t('validation:email_invalid')),
  password: z.string().min(1, t('validation:required')).min(8, t('validation:password_min')),
});

interface LoginFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

export function LoginForm({ onSuccess, redirectTo }: LoginFormProps) {
  const { login, error, clearError } = useAuth();
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loginSchema = createLoginSchema(t);
  type LoginFormData = z.infer<typeof loginSchema>;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Chromium-family browsers trigger a CSS animation on autofilled inputs.
  // Listening to onAnimationStart lets RHF capture the autofilled value before
  // the user clicks Submit (without this, RHF state stays empty while the
  // visible input shows the browser-filled text).
  const handleAutofill = (field: 'email' | 'password') =>
    (e: React.AnimationEvent<HTMLInputElement>) => {
      if (e.animationName === 'onAutoFillStart') {
        setValue(field, (e.currentTarget as HTMLInputElement).value, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    };

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    clearError();

    try {
      await login(data, redirectTo);
      onSuccess?.();
    } catch (err) {
      // Error is handled by useAuth hook
      // Clear password field on error
      reset({ email: data.email, password: '' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">{t('login.email_label')}</Label>
        <Input
          id="email"
          type="email"
          placeholder={t('login.email_placeholder')}
          autoComplete="username"
          disabled={isSubmitting}
          {...register('email')}
          onAnimationStart={handleAutofill('email')}
          aria-invalid={errors.email ? 'true' : 'false'}
        />
        {errors.email && (
          <p className="text-sm text-destructive" role="alert">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">{t('login.password_label')}</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder={t('login.password_placeholder')}
            autoComplete="current-password"
            disabled={isSubmitting}
            {...register('password')}
            onAnimationStart={handleAutofill('password')}
            aria-invalid={errors.password ? 'true' : 'false'}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            tabIndex={-1}
            aria-label={showPassword ? t('login.hide_password') : t('login.show_password')}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.password && (
          <p className="text-sm text-destructive" role="alert">
            {errors.password.message}
          </p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('login.submitting')}
          </>
        ) : (
          t('login.submit_button')
        )}
      </Button>

      <div className="text-center">
        <Link
          to="/forgot-password"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          {t('forgot_password.link')}
        </Link>
      </div>
    </form>
  );
}
